import { create } from 'zustand';
import type { FilterDefinition } from '@vivport/backend/types';
import { type Filters, tokenizeExploreQuery } from '@/utils/search-utils';
import { useSelectedItemStore } from './selectedItemStore';

interface SearchState {
  query: string;
  inputValue: string;
  filters: Filters;
  allTabFilters: string[];
  layoutMode: string;
  activeSavedFilterId: number | null;
  activeSavedFilterDefinition: FilterDefinition | null;
  activeSavedFilterName: string | null;
  setQuery: (newQuery: string) => void;
  setInputValue: (newInputValue: string, navigate: (path: string) => void, routePath?: string) => void;
  resetInputValue: (navigate: (path: string) => void, routePath?: string, defaultTabSearchFilter?: string) => void;
  clearInputValue: (navigate: (path: string) => void, routePath?: string) => void;
  setFilters: (newFilters: Filters) => void;
  setLayoutMode: (isContentDetailRoute: boolean) => void;
  setAllTabFilters: (allTabFilters: string[]) => void;
  setActiveSavedFilter: (filterId: number, definition: FilterDefinition, name: string) => void;
  clearActiveSavedFilter: () => void;
  isLayoutGlobal: () => boolean;
  isLayoutTabsSearching: () => boolean;
  isLayoutTabs: () => boolean;
  isLayoutDisplay: () => boolean;
  isCurrentTab: (searchFilter: string) => boolean;
}

const navigateExplore = '/explore'

export const useSearchStore = create<SearchState>((set, get) => {

  const updateLayoutMode = () => {
    const { filters, allTabFilters } = get();
    if (allTabFilters.includes(filters.tab ?? "")) {
      set({ layoutMode: "tabs" });
    } else if (filters.tab) {
      set({ layoutMode: "tabsSearching" });
    } else {
      set({ layoutMode: "global" });
    }
  };

  return {
    query: '',
    inputValue: '',
    filters: {},
    allTabFilters: [],
    layoutMode: 'global',
    activeSavedFilterId: null,
    activeSavedFilterDefinition: null,
    activeSavedFilterName: null,

    resetInputValue: (navigate: (path: string) => void, routePath?: string, defaultTabSearchFilter?: string) => {
      if (defaultTabSearchFilter) {
        set({ inputValue: defaultTabSearchFilter });
      } else {
        set({ inputValue: '' });
      }

      const { layoutMode } = get();
      if (routePath) {
        navigate(routePath);
      } else if (layoutMode === 'display') {
        navigate(navigateExplore);
      }
    },

    clearInputValue: (navigate: (path: string) => void, routePath?: string) => {
      set({ inputValue: '' });
      const { layoutMode } = get();
      if (routePath || layoutMode === 'display') {
        navigate(routePath || navigateExplore);
      }
      if (routePath && !routePath.includes('explore')) {
        set({ layoutMode: 'display' });
      }
    },

    setQuery: (newQuery: string) => {
      const tokens = tokenizeExploreQuery(newQuery);
      const newFilters: Filters = {
        ...(tokens.tabs.length > 0 ? { tab: tokens.tabs[0] } : {}),
        ...(tokens.searchText ? { include: tokens.searchText } : {}),
        ...(tokens.exclude.length > 0 ? { exclude: tokens.exclude.join(' ') } : {}),
        ...(tokens.after ? { after: tokens.after } : {}),
        ...(tokens.before ? { before: tokens.before } : {}),
        ...(tokens.createdBy.length > 0 ? { createdBy: tokens.createdBy } : {}),
        ...(tokens.mailbox ? { mailbox: true } : {}),
        ...(tokens.drives ? { drives: true } : {}),
      };

      set({ query: newQuery, filters: newFilters });
      updateLayoutMode();
    },

    setInputValue: (newInputValue: string, navigate: (path: string) => void, routePath?: string) => {
      set({ inputValue: newInputValue });
      const { layoutMode } = get();

      if (routePath) {
        navigate(routePath);
      } else if (layoutMode === 'display') {
        navigate(navigateExplore);
      }

      const selectedItemStore = useSelectedItemStore.getState();
      selectedItemStore.clearSelectedItems();
    },

    setFilters: (newFilters: Filters) => {
      set({ filters: newFilters });
      updateLayoutMode();
    },

    setLayoutMode: (isContentDetailRoute: boolean) => {
      if (isContentDetailRoute) {
        set({ layoutMode: 'display' });
        return;
      }
      // Use updateLayoutMode helper to compute based on filters and allTabFilters
      updateLayoutMode();
    },

    setAllTabFilters: (allTabFilters: string[]) => {
      set({ allTabFilters });
      updateLayoutMode();
    },

    setActiveSavedFilter: (filterId: number, definition: FilterDefinition, name: string) => {
      set({
        activeSavedFilterId: filterId,
        activeSavedFilterDefinition: definition,
        activeSavedFilterName: name
      });
    },

    clearActiveSavedFilter: () => {
      set({
        activeSavedFilterId: null,
        activeSavedFilterDefinition: null,
        activeSavedFilterName: null
      });
    },

    isLayoutGlobal: () => {
      return get().layoutMode === 'global';
    },
    
    isLayoutTabs: () => {
      return get().layoutMode === 'tabs';
    },
    
    isLayoutTabsSearching: () => {
      return get().layoutMode === 'tabsSearching';
    },

    isLayoutDisplay: () => {
      return get().layoutMode === 'display';
    },

    isCurrentTab: (searchFilter: string) => {
      const { query } = get();
      return query.includes(searchFilter.trim());
    },
  };
});
